import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Spinner } from '@easydent/shared-ui';
import Layout from '@/components/Layout';
import { getWorklist, type ReferralStatus, type WorklistItem } from '@/lib/api';

/** The three states a centre actually works in. Everything else is history. */
const TABS: { key: 'new' | 'accepted' | 'done'; status: ReferralStatus }[] = [
  { key: 'new', status: 'Requested' },
  { key: 'accepted', status: 'Accepted' },
  { key: 'done', status: 'Completed' },
];

const WorklistPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'new' | 'accepted' | 'done'>('new');
  const [items, setItems] = useState<WorklistItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(null); setError(null);
    getWorklist(TABS.find((x) => x.key === tab)!.status)
      .then(setItems)
      .catch(() => setError(t('worklist.loadError', 'Could not load your referrals.')));
  }, [tab, t]);

  const day = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

  /** Past due is worth shouting about: the patient was expected and hasn't come. */
  const overdue = (dueDate: string) => new Date(dueDate) < new Date(new Date().toDateString());

  return (
    <Layout>
      <h1 className="mb-4 text-2xl font-bold text-body">{t('worklist.title', 'Referrals')}</h1>

      <div className="mb-4 flex gap-1">
        {TABS.map((x) => (
          <button
            key={x.key}
            onClick={() => setTab(x.key)}
            className={[
              'rounded-lg px-3 py-1.5 text-sm font-medium',
              tab === x.key ? 'bg-brand-600 text-white' : 'bg-surface text-muted-strong hover:bg-subtle',
            ].join(' ')}
          >
            {t(`worklist.tab.${x.key}`, x.key)}
          </button>
        ))}
      </div>

      {error && <Alert variant="error" className="mb-3">{error}</Alert>}

      {items === null ? (
        <div className="flex justify-center py-10"><Spinner size="lg" /></div>
      ) : items.length === 0 ? (
        <Card><p className="text-sm text-muted">{t('worklist.empty', 'Nothing here at the moment.')}</p></Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <button key={r.id} onClick={() => navigate(`/referrals/${r.id}`)} className="block w-full text-left">
              <Card className="transition hover:border-brand-300 hover:shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-[14rem] flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-body">{r.patientDisplayName}</span>
                      {r.isUrgent && (
                        <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                          {t('worklist.urgent', 'Urgent')}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm text-muted-strong">{r.summary}</div>
                    <div className="mt-1 text-xs text-subtle-ink">{r.clinicName}</div>
                  </div>
                  <div className="shrink-0 text-right text-xs">
                    <div className={overdue(r.dueDate) ? 'font-semibold text-red-600' : 'text-muted'}>
                      {t('worklist.by', 'by {{date}}', { date: day(r.dueDate) })}
                    </div>
                    {r.fileCount > 0 && (
                      <div className="mt-1 text-subtle-ink">
                        {t('worklist.files', '{{count}} file', { count: r.fileCount })}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
};

export default WorklistPage;
